/* global google */
import { GOOGLE_PHOTOS_TOKEN } from "utils/localStorage";

import { jjLogger } from "./logUtils";

const PICKER_API_BASE_URL = "https://photospicker.googleapis.com/v1";
const OAUTH_SCOPE = "https://www.googleapis.com/auth/photospicker.mediaitems.readonly";
export const IS_GOOGLE_PHOTOS_ENABLED = false;

export const clearToken = () => {
  localStorage.removeItem(GOOGLE_PHOTOS_TOKEN);
};

export const storeToken = (responseToken) => {
  localStorage.setItem(GOOGLE_PHOTOS_TOKEN, JSON.stringify(responseToken));
};

export const getToken = () => {
  try {
    const existingToken = localStorage.getItem(GOOGLE_PHOTOS_TOKEN);
    if (!existingToken) return null;

    const tokenData = JSON.parse(existingToken);
    const expiresInMs = tokenData.expiresIn * 1000;
    const age = Date.now() - tokenData.timestamp;
    const isExpired = age >= expiresInMs;

    if (isExpired) {
      clearToken();
      return null;
    }

    return tokenData.accessToken;
  } catch (error) {
    jjLogger.logError(`googlePhotosUtils: getToken error: ${error}`);
    return null;
  }
};

export const getValidatedToken = () => {
  const token = getToken();
  if (!token) {
    throw new Error("Google Photos access token is missing or expired");
  }
  return token;
};

export const initOAuth2Client = (clientId, callback) => {
  if (typeof google === "undefined" || !google.accounts?.oauth2) {
    throw new Error("Google Identity Services library not loaded");
  }

  const client = google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: OAUTH_SCOPE,
    callback: (tokenResponse) => {
      if (tokenResponse && tokenResponse.access_token) {
        // Verify the required scope was granted
        if (google.accounts.oauth2.hasGrantedAllScopes(tokenResponse, OAUTH_SCOPE)) {
          const tokenToStore = {
            ...tokenResponse,
            accessToken: tokenResponse.access_token,
            expiresIn: tokenResponse.expires_in,
            timestamp: Date.now(),
          };

          storeToken(tokenToStore);
          callback(null);
        } else {
          callback(new Error("Required Google Photos scope not granted"));
        }
      } else if (tokenResponse?.error) {
        callback(new Error(tokenResponse.error_description || tokenResponse.error));
      } else {
        callback(new Error("Unexpected response from Google OAuth token endpoint"));
      }
    },
  });
  return client;
};

export const createPickerSession = async () => {
  try {
    const response = await fetch(`${PICKER_API_BASE_URL}/sessions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getValidatedToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pickingConfig: {
          maxItemCount: 1,
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create picker session: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    return {
      pickerUri: data.pickerUri,
      sessionId: data.id,
      pollingConfig: data.pollingConfig,
    };
  } catch (error) {
    jjLogger.logError(`googlePhotosUtils: createPickerSession error: ${error}`);
    throw error;
  }
};

const parseDurationToMs = (duration) => {
  const seconds = parseFloat(duration);
  return seconds * 1000;
};

export const pollSession = async (sessionId, pollingConfig) => {
  if (!pollingConfig?.pollInterval || !pollingConfig?.timeoutIn) {
    throw new Error("pollingConfig with pollInterval and timeoutIn is required");
  }

  const intervalMs = parseDurationToMs(pollingConfig.pollInterval);
  const timeoutMs = parseDurationToMs(pollingConfig.timeoutIn);
  const startTime = Date.now();

  const checkSession = async () => {
    // Check timeout
    if (Date.now() - startTime > timeoutMs) {
      throw new Error("Session polling timeout - selection took too long");
    }

    try {
      const response = await fetch(`${PICKER_API_BASE_URL}/sessions/${sessionId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getValidatedToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to poll session: ${response.status}`);
      }

      const data = await response.json();

      // If user has selected media, we're done
      if (data.mediaItemsSet) {
        return true;
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      return checkSession();
    } catch (error) {
      jjLogger.logError(`googlePhotosUtils: pollSession error: ${error}`);
      throw error;
    }
  };

  return checkSession();
};

export const getSelectedMediaItems = async (sessionId) => {
  try {
    const response = await fetch(`${PICKER_API_BASE_URL}/mediaItems?sessionId=${sessionId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getValidatedToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get media items: ${response.status}`);
    }

    const data = await response.json();
    return data.mediaItems || [];
  } catch (error) {
    jjLogger.logError(`googlePhotosUtils: getSelectedMediaItems error: ${error}`);
    throw error;
  }
};

export const getFirstSelectedMediaFile = (mediaItems) => {
  if (!mediaItems || mediaItems.length === 0) {
    throw new Error("No photos selected");
  }

  return mediaItems[0].mediaFile;
};

export const isAnImageMediaFile = (mediaFile) => Boolean(mediaFile?.mimeType?.startsWith("image/"));

export const deleteSession = async (sessionId) => {
  try {
    const response = await fetch(`${PICKER_API_BASE_URL}/sessions/${sessionId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getValidatedToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete session: ${response.status}`);
    }

    return true;
  } catch (error) {
    jjLogger.logError(`googlePhotosUtils: deleteSession error: ${error}`);
    throw error;
  }
};

const validateMediaFile = (mediaFile) => {
  if (!mediaFile || !mediaFile?.baseUrl) {
    throw new Error(`Mediafile is invalid: ${JSON.stringify(mediaFile)}`);
  }

  if (!isAnImageMediaFile(mediaFile)) {
    throw new Error(
      `Unsupported file type selected from Google Photos. mediaFile.mimeType received: ${mediaFile.mimeType}`,
    );
  }
};

export const fetchMediaAsBase64 = async (mediaFile) => {
  try {
    validateMediaFile(mediaFile);

    const imageUrl = `${mediaFile.baseUrl}=w2048-h2048`;

    const response = await fetch(imageUrl, {
      headers: { Authorization: `Bearer ${getValidatedToken()}` },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to convert image to base64"));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    jjLogger.logError(`googlePhotosUtils: fetchMediaAsBase64 error: ${error}`);
    throw error;
  }
};
